import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { ChevronLeft, ChevronRight } from 'lucide-react';

interface CarouselItem {
  id: number;
  title: string;
  subtitle: string;
  image_url: string;
  product_link: string;
  is_active: boolean;
  display_order: number;
}

const AdminCarouselBanner: React.FC = () => {
  const [carouselItems, setCarouselItems] = useState<CarouselItem[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [isHovered, setIsHovered] = useState(false);

  useEffect(() => {
    fetchCarouselItems();
  }, []);

  const fetchCarouselItems = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('carousel' as any)
        .select('*')
        .eq('is_active', true)
        .order('display_order', { ascending: true });

      if (error) {
        console.error('Error fetching carousel items:', error);
        return;
      }

      if (data && data.length > 0) {
        setCarouselItems(data as any[]);
      }
    } catch (error) {
      console.error('Error in fetchCarouselItems:', error);
    } finally {
      setLoading(false);
    }
  };

  // Auto-advance carousel
  useEffect(() => {
    if (isHovered || carouselItems.length === 0) return;

    const timer = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % carouselItems.length);
    }, 5000); // Change slide every 5 seconds

    return () => clearInterval(timer);
  }, [isHovered, carouselItems.length]);

  const goToNext = () => {
    setCurrentIndex((prev) => (prev + 1) % carouselItems.length);
  };

  const goToPrevious = () => {
    setCurrentIndex((prev) => (prev - 1 + carouselItems.length) % carouselItems.length);
  };

  const goToSlide = (index: number) => {
    setCurrentIndex(index);
  };

  if (loading || carouselItems.length === 0) {
    return null; // Don't show anything if loading or no items
  }

  const currentItem = carouselItems[currentIndex];

  return (
    <section className="w-full bg-cream-50 py-3">
      <div className="container-custom">
        <div
          className="relative w-full rounded-xl overflow-hidden shadow-lg group"
          style={{ aspectRatio: '3240 / 537' }}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        >
          {/* Carousel Images */}
          <div className="relative w-full h-full">
            {carouselItems.map((item, index) => (
              <div
                key={item.id}
                className={`absolute inset-0 transition-opacity duration-500 ${
                  index === currentIndex ? 'opacity-100' : 'opacity-0'
                }`}
              >
                {item.product_link ? (
                  <Link to={item.product_link} className="block w-full h-full">
                    <img
                      src={item.image_url}
                      alt={item.title}
                      className="w-full h-full object-cover"
                    />
                    {/* Overlay with text */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent flex items-end">
                      <div className="p-3 sm:p-4 md:p-6 text-white">
                        <h2 className="text-base sm:text-lg md:text-xl font-bold font-playfair mb-1">
                          {item.title}
                        </h2>
                        {item.subtitle && (
                          <p className="text-xs sm:text-sm text-cream-100">
                            {item.subtitle}
                          </p>
                        )}
                      </div>
                    </div>
                  </Link>
                ) : (
                  <>
                    <img
                      src={item.image_url}
                      alt={item.title}
                      className="w-full h-full object-cover"
                    />
                    {/* Overlay with text */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent flex items-end">
                      <div className="p-3 sm:p-4 md:p-6 text-white">
                        <h2 className="text-base sm:text-lg md:text-xl font-bold font-playfair mb-1">
                          {item.title}
                        </h2>
                        {item.subtitle && (
                          <p className="text-xs sm:text-sm text-cream-100">
                            {item.subtitle}
                          </p>
                        )}
                      </div>
                    </div>
                  </>
                )}
              </div>
            ))}
          </div>

          {/* Navigation Arrows */}
          {carouselItems.length > 1 && (
            <>
              <button
                onClick={goToPrevious}
                className="absolute left-2 sm:left-4 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white text-brand-800 p-2 rounded-full shadow-lg transition-all duration-150 hover:scale-110"
                aria-label="Previous slide"
              >
                <ChevronLeft className="h-4 w-4 sm:h-5 sm:w-5" />
              </button>
              <button
                onClick={goToNext}
                className="absolute right-2 sm:right-4 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white text-brand-800 p-2 rounded-full shadow-lg transition-all duration-150 hover:scale-110"
                aria-label="Next slide"
              >
                <ChevronRight className="h-4 w-4 sm:h-5 sm:w-5" />
              </button>
            </>
          )}

          {/* Dot Indicators */}
          {carouselItems.length > 1 && (
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex space-x-2">
              {carouselItems.map((_, index) => (
                <button
                  key={index}
                  onClick={() => goToSlide(index)}
                  className={`w-2 h-2 rounded-full transition-all duration-150 ${
                    index === currentIndex
                      ? 'bg-white w-6'
                      : 'bg-white/60 hover:bg-white/80'
                  }`}
                  aria-label={`Go to slide ${index + 1}`}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default AdminCarouselBanner;
